import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { conversationAPI, conversationStartersAPI, childrenAPI, metricsAPI } from '../services/api';
import ActivityCard from '../components/ActivityCard';
import './Chat.css';

const Chat = () => {
  const { childId } = useParams();
  const navigate = useNavigate();

  // Generate unique session ID when chat starts
  const [sessionId] = useState(() => {
    // Generate UUID v4
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  });

  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [childName, setChildName] = useState('');
  const [conversationStarters, setConversationStarters] = useState([]);
  const [categorizedStarters, setCategorizedStarters] = useState(null);
  const [activeStarterTab, setActiveStarterTab] = useState('fun_topics');
  const [contextualStarters, setContextualStarters] = useState([]);
  const [showHelpGuidance, setShowHelpGuidance] = useState(false);
  const [loadingHelp, setLoadingHelp] = useState(false);
  const [error, setError] = useState('');

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  // Load child info and conversation history
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);

        // Get child info
        const childResponse = await childrenAPI.getById(childId);
        setChildName(childResponse.data.name);

        // Get conversation history
        const historyResponse = await conversationAPI.getHistory(childId, 50);
        setMessages(historyResponse.data.conversations || []);

        // Get categorized conversation starters if no messages yet
        if (!historyResponse.data.conversations || historyResponse.data.conversations.length === 0) {
          const startersResponse = await conversationStartersAPI.getCategorized(childId);
          setCategorizedStarters(startersResponse.data.starters || null);
        }
      } catch (err) {
        console.error('Error loading chat data:', err);
        setError('Could not load chat. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    if (childId) {
      loadData();
    }

    // Cleanup: Update session metrics when leaving the chat
    // Note: This cleanup only runs when the component unmounts (when leaving the page)
    return () => {
      if (childId && sessionId) {
        // Update session metrics in the background (don't await)
        metricsAPI.updateSession(childId, sessionId).catch(err => {
          console.error('Failed to update session metrics:', err);
        });
      }
    };
  }, [childId, sessionId]);

  // Fetch contextual help when user clicks "Help me respond"
  const handleHelpMeRespond = async () => {
    if (messages.length < 2) {
      // Need at least some conversation context
      return;
    }

    // Track help button click for metrics
    try {
      await metricsAPI.trackHelpClick(childId, sessionId);
    } catch (err) {
      // Don't block the UI if tracking fails
      console.error('Failed to track help button click:', err);
    }

    setLoadingHelp(true);
    try {
      // Get last 5 messages for context
      const recentMessages = messages.slice(-5).map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      const response = await conversationStartersAPI.getContextual(
        childId,
        messages.length,
        recentMessages
      );
      setContextualStarters(response.data.starters || []);
      setShowHelpGuidance(true);
    } catch (err) {
      console.error('Failed to fetch help guidance:', err);
    } finally {
      setLoadingHelp(false);
    }
  };

  const handleSendMessage = async (messageText = null) => {
    const textToSend = messageText || inputMessage.trim();

    if (!textToSend) return;

    // Clear input, starters, and help guidance
    setInputMessage('');
    setConversationStarters([]);
    setCategorizedStarters(null);
    setShowHelpGuidance(false);
    setError('');

    // Add user message to UI immediately
    const userMessage = {
      role: 'user',
      content: textToSend,
      created_at: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMessage]);

    // Show typing indicator
    setIsTyping(true);

    try {
      // Send message to backend
      const response = await conversationAPI.sendMessage(childId, textToSend, sessionId);

      // Add AI response to messages
      const aiMessage = {
        role: 'assistant',
        content: response.data.response,
        created_at: new Date().toISOString(),
      };
      setMessages(prev => [...prev, aiMessage]);
    } catch (err) {
      console.error('Error sending message:', err);

      // Show error message
      let errorMsg = 'Oops! Something went wrong. Please try again.';
      if (err.response?.data?.detail) {
        errorMsg = err.response.data.detail;
      }
      setError(errorMsg);

      // Remove the user message if send failed
      setMessages(prev => prev.filter(msg => msg !== userMessage));
    } finally {
      setIsTyping(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleStarterClick = (starter) => {
    // Handle both old format (object with text) and new format (string)
    const text = typeof starter === 'string' ? starter : starter.text;
    handleSendMessage(text);
  };

  if (isLoading) {
    return (
      <div className="chat-container">
        <div className="chat-loading">
          <div className="loading-spinner"></div>
          <p>Loading chat...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-container">
      {/* Header */}
      <div className="chat-header">
        <button className="back-button" onClick={() => navigate('/dashboard')}>
          ← Back
        </button>
        <div className="chat-title">
          <div className="avatar">🤖</div>
          <div>
            <h1>AI Friend</h1>
            <p className="chat-subtitle">Chatting with {childName}</p>
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <div className="messages-area">
        {messages.length === 0 && !categorizedStarters && conversationStarters.length === 0 && (
          <div className="welcome-message">
            <div className="big-avatar">🤖</div>
            <h2>Hello {childName}! 👋</h2>
            <p>I'm your AI friend. Let's chat about anything you like!</p>
          </div>
        )}

        {/* Categorized Conversation Starters (at start) */}
        {categorizedStarters && messages.length === 0 && (
          <div className="starters-container">
            <div className="starter-tabs">
              <button
                className={activeStarterTab === 'feelings' ? 'tab active' : 'tab'}
                onClick={() => setActiveStarterTab('feelings')}
              >
                💭 Feelings
              </button>
              <button
                className={activeStarterTab === 'activities' ? 'tab active' : 'tab'}
                onClick={() => setActiveStarterTab('activities')}
              >
                🎮 Activities
              </button>
              <button
                className={activeStarterTab === 'fun_topics' ? 'tab active' : 'tab'}
                onClick={() => setActiveStarterTab('fun_topics')}
              >
                ⭐ Fun Topics
              </button>
              <button
                className={activeStarterTab === 'questions' ? 'tab active' : 'tab'}
                onClick={() => setActiveStarterTab('questions')}
              >
                ❓ Questions
              </button>
            </div>

            <div className="starters-grid">
              {categorizedStarters[activeStarterTab]?.map((starter, idx) => (
                <div
                  key={idx}
                  className="starter-guidance"
                >
                  💡 {starter}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Old conversation starters (fallback for compatibility) */}
        {conversationStarters.length > 0 && (
          <div className="starters-container">
            <p className="starters-label">Here are some ideas to get started:</p>
            <div className="starters-grid">
              {conversationStarters.map((starter, index) => (
                <button
                  key={index}
                  className="starter-button"
                  onClick={() => handleStarterClick(starter)}
                >
                  {starter.text}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Message Bubbles */}
        {messages.map((message, index) => {
          // Check if AI message contains an activity suggestion
          const activityMatch = message.role === 'assistant' &&
            message.content.match(/ACTIVITY:\s*([^\n]+)\n([^\n]+(?:\n(?!ACTIVITY:)[^\n]+)*)/);

          if (activityMatch) {
            // Parse activity from message
            const titleWithEmoji = activityMatch[1].trim();
            const description = activityMatch[2].trim();
            const textBefore = message.content.substring(0, activityMatch.index).trim();
            const textAfter = message.content.substring(activityMatch.index + activityMatch[0].length).trim();

            return (
              <div key={index}>
                {/* Regular message text before activity */}
                {textBefore && (
                  <div className={`message-bubble ai-message`}>
                    <div className="message-avatar">🤖</div>
                    <div className="message-content">
                      <p>{textBefore}</p>
                    </div>
                  </div>
                )}

                {/* Activity Card */}
                <div className="message-bubble ai-message">
                  <div className="message-avatar">🤖</div>
                  <ActivityCard title={titleWithEmoji} description={description} />
                </div>

                {/* Regular message text after activity */}
                {textAfter && (
                  <div className={`message-bubble ai-message`}>
                    <div className="message-avatar">🤖</div>
                    <div className="message-content">
                      <p>{textAfter}</p>
                    </div>
                  </div>
                )}
              </div>
            );
          }

          // Regular message (no activity)
          return (
            <div
              key={index}
              className={`message-bubble ${message.role === 'user' ? 'user-message' : 'ai-message'}`}
            >
              {message.role === 'assistant' && (
                <div className="message-avatar">🤖</div>
              )}
              <div className="message-content">
                <p>{message.content}</p>
              </div>
              {message.role === 'user' && (
                <div className="message-avatar user-avatar">😊</div>
              )}
            </div>
          );
        })}

        {/* Typing Indicator */}
        {isTyping && (
          <div className="message-bubble ai-message typing-indicator">
            <div className="message-avatar">🤖</div>
            <div className="message-content">
              <div className="typing-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="error-message">
            <p>⚠️ {error}</p>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="input-area">
        {/* Help Guidance (when requested) */}
        {showHelpGuidance && contextualStarters.length > 0 && (
          <div className="help-guidance-container">
            <div className="guidance-header">
              <span className="guidance-icon">💡</span>
              <span className="guidance-title">Here are some ways you could respond:</span>
              <button
                className="dismiss-guidance"
                onClick={() => setShowHelpGuidance(false)}
              >
                ✕
              </button>
            </div>
            <div className="guidance-suggestions">
              {contextualStarters.map((starter, idx) => (
                <div key={idx} className="guidance-suggestion">
                  {starter}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Help Me Respond Button */}
        {messages.length >= 2 && !showHelpGuidance && (
          <button
            className="help-me-respond-button"
            onClick={handleHelpMeRespond}
            disabled={loadingHelp || isTyping}
          >
            {loadingHelp ? '⏳ Getting suggestions...' : '💬 Help me respond'}
          </button>
        )}

        <div className="input-container">
          <textarea
            ref={inputRef}
            className="message-input"
            placeholder="Type your message here..."
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            rows="1"
            disabled={isTyping}
          />
          <button
            className="send-button"
            onClick={() => handleSendMessage()}
            disabled={!inputMessage.trim() || isTyping}
          >
            <span className="send-icon">📤</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Chat;
